import GlobalModalProvider from '@components/cells/global-modal-provider';
import NotificationsProviderContainer from '@components/cells/notifications-provider-container';
import ZendeskPopover from '@components/molecules/zendesk-popover/zendesk-popover';
import { GoogleReCaptchaProvider } from '@google-recaptcha/react';
import configureI18n from '@helpers/i18n';
import { configureYup } from '@helpers/yup';
import useAuth from '@hooks/useAuth';
import { FeatureToggleProvider } from '@minds/feature_toggle_react_client';
import { StyleProvider } from '@minds/minds-react-sdk';
import { store } from '@stateAtoms/store';
import styles from '@styles/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@unocss/reset/normalize.css';
import { Provider } from 'jotai';
import mixpanel from 'mixpanel-browser';
import { useState } from 'react';
import 'react-day-picker/dist/style.css';
import ReactDOM from 'react-dom/client';
import Routes from './routes';
// eslint-disable-next-line import/no-unresolved
import 'virtual:uno.css';
import { Config, loadConfig } from './helpers/config';
import './styles/styles.scss';

const OLD_HOSTNAME = 'clients';
const HOSTNAME = 'app';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            retry: (count) => count < 3,
            staleTime: 1000 * 5,
        },
    },
});

function App({ config }: { config: Config }) {
    const [loadedI18n, setLoadedI18n] = useState(false);

    if (!loadedI18n) {
        void configureI18n().then(() => {
            configureYup();
            setLoadedI18n(true);
        });
    }

    return (
        <Provider store={store}>
            <FeatureToggleProvider
                url={config.featureToggleUrl}
                environment={config.env}
                queryClient={queryClient}
            >
                <StyleProvider styles={styles}>
                    <GoogleReCaptchaProvider
                        type="v2-checkbox"
                        siteKey={config.recaptchaKey}
                        isEnterprise
                    >
                        <NotificationsProviderContainer />
                        <GlobalModalProvider />

                        <QueryClientProvider client={queryClient}>
                            {loadedI18n ? <InnerApp /> : null}
                        </QueryClientProvider>
                    </GoogleReCaptchaProvider>
                </StyleProvider>
            </FeatureToggleProvider>
        </Provider>
    );
}

function InnerApp() {
    const [loadedAuth, setLoadedAuth] = useState(false);
    const auth = useAuth();

    if (!loadedAuth) {
        void auth.checkUserEnvironment().then(() => {
            setLoadedAuth(true);
        });
    }

    return loadedAuth ? (
        <>
            <Routes />

            <ZendeskPopover />
        </>
    ) : null;
}

window.global ||= window;

void loadConfig().then((config) => {
    mixpanel.init(config.mixpanelToken);

    const { hostname } = window.location;
    if (hostname.includes(OLD_HOSTNAME)) {
        const newHostname = hostname.replace(OLD_HOSTNAME, HOSTNAME);
        window.location.hostname = newHostname;
        return;
    }

    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
        <App config={config} />,
    );
});
